/**
 * Warden — the services organization.
 *
 * Palo Alto fronts its services menu with Unit 42 and an "Under Attack?"
 * incident hotline. That works because they staff one. Warden's equivalent slot
 * is a briefing request, and the copy sells scheduled engagements rather than
 * any response capability.
 */

import type { WardenHubContent } from "./types";

export const WARDEN_HUB: WardenHubContent = {
  path: "/warden",
  headline:
    "The engineers who build our runtime security work on your agent estate",
  lede:
    "Warden is Odingard's services organization. Same people, same research, applied to the systems you are already running — red-teaming autonomous agents, constraining what they can reach, and turning the results into evidence an auditor accepts.",
  ctas: [
    { label: "Request a briefing", href: "/contact?topic=warden" },
    { label: "Explore the practices", href: "#practices" },
  ],
  premise: {
    heading: "Assurance built for software that waits is the wrong tool here",
    body:
      "A traditional assessment describes an application that does what it is told. An agent chooses its next action from context it was handed at runtime, which means the interesting question is not what the code can do but what the agent can be persuaded to do with the credentials you gave it.",
    points: [
      {
        title: "The boundary is the tool call, not the perimeter",
        body: "Network controls do not see an agent reading a document and then calling an API with what it just read.",
      },
      {
        title: "Findings expire faster than reports",
        body: "A point-in-time assessment of a system whose behavior depends on its prompt is stale before it is formatted.",
      },
      {
        title: "Regulators want evidence, not assertions",
        body: "The EU AI Act, ISO/IEC 42001 and NIST AI RMF all ask what you can show, not what you have documented.",
      },
    ],
  },
  method: {
    heading: "How a Warden engagement runs",
    lede:
      "Every practice follows the same shape, because the value is in the sequence: find out what is true, prove it adversarially, fix the boundary, then leave the evidence running.",
    steps: [
      {
        title: "Establish the estate",
        body: "Inventory the agents, models, tools and data paths actually in use — including the ones nobody registered.",
      },
      {
        title: "Test it adversarially",
        body: "Attack the agents with the same techniques we research, so the failure mode surfaces in an exercise rather than in production.",
      },
      {
        title: "Constrain the boundary",
        body: "Put the enforceable control where the action happens, then confirm the attack that worked no longer does.",
      },
      {
        title: "Leave evidence behind",
        body: "Wire the result into continuous monitoring so the next auditor question is answered by a system, not by a person remembering.",
      },
    ],
  },
  close: {
    heading: "Start with a briefing, not a statement of work",
    body:
      "Tell us what your agents can reach today. We will tell you which of these engagements is worth doing first, and which ones you do not need yet.",
    ctas: [
      { label: "Request a briefing", href: "/contact?topic=warden" },
      { label: "See how we secure agents", href: "/secure-agentic-ai" },
    ],
  },
};

/** Short descriptions for the five practices, keyed by WARDEN_SERVICES title. */
export const WARDEN_PRACTICES: Record<string, string> = {
  "Agentic Assurance":
    "Find out what your agents can actually be made to do, before someone else does.",
  "AI Governance":
    "Get an AI program that satisfies the EU AI Act, ISO/IEC 42001 and NIST AI RMF on evidence rather than intent.",
  "Continuous Compliance":
    "Run the control monitoring and evidence operation without hiring a team to do it.",
  Deployment:
    "Get Cerberus and TraceLock into production, integrated with what you already run.",
  Advisory:
    "Standing access to the people who do this work, for the decisions that do not wait for an engagement.",
};
